import { useSingleOpenLinkPopover } from '@/atoms/link-popover-open';
import {
  IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/components/constants/flags';
import { useSidebarPanelOverlayLock } from '@/components/layout/Navigation/sidebar-panel/hooks/use-sidebar-panel-overlay-lock';
import {
  getSidebarPanelOverlayOpenProps,
  getSidebarPanelOverlayTriggerProps,
} from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { PatientProfileList } from '@/components/ui/responsive-combobox/patient-profile-list';
import { SuggestedMatchReviewDialog } from '@/features/patient-find-or-link/components/patient-search-combobox/suggested-match-review';
import { toSuggestedMatchPatientProfile } from '@/features/patient-find-or-link/utils/to-suggested-match-patient-profile';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import type { PatientOutcomeSurface } from '@/models/analytics/features/patient';
import { Drawer, DrawerContent, DrawerTrigger } from '@heidi/design-system/drawer';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyPBold } from '@heidi/design-system/typography';
import type { SuggestedPatientMatch } from '@heidi/patients';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import type { PatientProfileAppointmentCandidates } from '@heidi/session/contracts';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface ComboBoxResponsiveProps {
  trigger: React.ReactNode;
  tooltipContent?: string;
  popoverAlign?: 'start' | 'center' | 'end';
  popoverSide?: 'top' | 'right' | 'bottom' | 'left';
  popoverAlignOffset?: number;
  popoverSideOffset?: number;
  commandInputPlaceholder?: string;
  showTooltip?: boolean;
  className?: string;
  selectedSessionIds: string[];
  disabled?: boolean;
  patientProfileAppointmentCandidates?: PatientProfileAppointmentCandidates | null;
  /** `patient_linked` surface attribution; omitted → the event fires bare. */
  trackingSurface?: PatientOutcomeSurface;
}

export function LinkSessionsComboBoxResponsive({
  trigger,
  tooltipContent,
  popoverAlign,
  popoverSide,
  popoverAlignOffset,
  popoverSideOffset,
  commandInputPlaceholder,
  showTooltip,
  className,
  selectedSessionIds,
  disabled,
  patientProfileAppointmentCandidates,
  trackingSurface,
}: ComboBoxResponsiveProps) {
  // Coordinated so only one link popover is open at a time (see
  // useSingleOpenLinkPopover).
  const [open, setOpen] = useSingleOpenLinkPopover();
  const [isReviewingSuggestedMatches, setIsReviewingSuggestedMatches] = useState(false);
  const [reviewSuggestedMatches, setReviewSuggestedMatches] = useState<
    readonly SuggestedPatientMatch[]
  >([]);
  const isMobile = useIsMobile();
  const currentSessionId = useCurrentSessionId();
  const { mutate: linkSessionsToPatientProfile } =
    useMutateLinkSessionsToPatientProfile();
  const isConsolidatedSessionsEnabled = IS_CONSOLIDATED_SESSIONS_LIST_ENABLED();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const sidebarPanelOverlayTriggerProps =
    getSidebarPanelOverlayTriggerProps(isSidebarPanelEnabled);
  const sidebarPanelOverlayOpenProps = getSidebarPanelOverlayOpenProps({
    isSidebarPanelEnabled,
    isOpen: open,
  });

  useSidebarPanelOverlayLock(open);

  const handleReviewSuggestedMatches = useCallback(
    (matches: readonly SuggestedPatientMatch[]) => {
      setReviewSuggestedMatches(matches);
      setOpen(false);
      setIsReviewingSuggestedMatches(true);
    },
    [setOpen]
  );

  const closeSuggestedMatchReview = useCallback(() => {
    setReviewSuggestedMatches([]);
    setIsReviewingSuggestedMatches(false);
  }, []);

  const handleReviewDialogOpenChange = useCallback((nextOpen: boolean) => {
    setIsReviewingSuggestedMatches(nextOpen);
    if (!nextOpen) {
      setReviewSuggestedMatches([]);
    }
  }, []);

  const patientProfileAppointmentCandidatesForSelection =
    selectedSessionIds.length === 1 ? patientProfileAppointmentCandidates : null;

  const handleConfirmSuggestedMatch = useCallback(
    (match: SuggestedPatientMatch) => {
      // Plain link (related-sessions merge dialog + link-and-merge API removed
      // in APP-7134).
      const patientProfile = toSuggestedMatchPatientProfile(match);
      linkSessionsToPatientProfile({
        patientProfile,
        sessionIds: selectedSessionIds,
        sessionId: currentSessionId || undefined,
        ...(trackingSurface && { tracking: { surface: trackingSurface } }),
      });
      closeSuggestedMatchReview();
    },
    [
      closeSuggestedMatchReview,
      currentSessionId,
      linkSessionsToPatientProfile,
      selectedSessionIds,
      trackingSurface,
    ]
  );

  const reviewDialog = (
    <SuggestedMatchReviewDialog
      open={isReviewingSuggestedMatches}
      onOpenChange={handleReviewDialogOpenChange}
      matches={reviewSuggestedMatches}
      onDismiss={closeSuggestedMatchReview}
      onConfirmMatch={handleConfirmSuggestedMatch}
    />
  );

  if (!isMobile) {
    const popover = showTooltip ? (
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              className={cn(
                'flex items-center',
                isConsolidatedSessionsEnabled ? 'shrink-0' : 'w-full'
              )}
              asChild
            >
              <PopoverTrigger
                {...sidebarPanelOverlayTriggerProps}
                {...sidebarPanelOverlayOpenProps}
                asChild
                disabled={disabled}
                className={cn(!isConsolidatedSessionsEnabled && 'w-full')}
              >
                {trigger}
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">
              <TypographyCaption>{tooltipContent}</TypographyCaption>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
          <PopoverContent
            className={cn('w-[250px] p-0', className)}
            align={popoverAlign || 'start'}
            side={popoverSide || 'bottom'}
            alignOffset={popoverAlignOffset || 0}
            sideOffset={popoverSideOffset || 0}
          >
            <PatientProfileList
              setOpen={setOpen}
              trackingSurface={trackingSurface}
              commandInputPlaceholder={commandInputPlaceholder}
              selectedSessionIds={selectedSessionIds}
              disabled={disabled}
              showSuggestedMatches
              onReviewSuggestedMatches={handleReviewSuggestedMatches}
              patientProfileAppointmentCandidates={
                patientProfileAppointmentCandidatesForSelection
              }
            />
          </PopoverContent>
        </TooltipProvider>
      </Popover>
    ) : (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          {...sidebarPanelOverlayTriggerProps}
          {...sidebarPanelOverlayOpenProps}
          className={cn(isConsolidatedSessionsEnabled ? 'shrink-0' : 'w-full')}
          asChild
          disabled={disabled}
        >
          {trigger}
        </PopoverTrigger>
        <PopoverContent
          className={cn('w-[250px] p-0', className)}
          align={popoverAlign || 'start'}
          side={popoverSide || 'bottom'}
          alignOffset={popoverAlignOffset || 0}
          sideOffset={popoverSideOffset || 0}
        >
          <PatientProfileList
            setOpen={setOpen}
            trackingSurface={trackingSurface}
            commandInputPlaceholder={commandInputPlaceholder}
            selectedSessionIds={selectedSessionIds}
            disabled={disabled}
            showSuggestedMatches
            onReviewSuggestedMatches={handleReviewSuggestedMatches}
            patientProfileAppointmentCandidates={
              patientProfileAppointmentCandidatesForSelection
            }
          />
        </PopoverContent>
      </Popover>
    );

    return (
      <>
        {popover}
        {reviewDialog}
      </>
    );
  }

  return (
    <>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger
          {...sidebarPanelOverlayTriggerProps}
          {...sidebarPanelOverlayOpenProps}
          asChild
          disabled={disabled}
        >
          {trigger}
        </DrawerTrigger>
        <DrawerContent className="flex w-full gap-3 p-2">
          <div
            className="absolute left-2 top-2 flex items-center gap-0.5"
            onClick={() => setOpen(false)}
          >
            <ChevronLeft size={18} className="text-text-active rtl:-scale-x-100" />
            <TypographyPBold className="font-normal text-text-active">
              <FormattedMessage
                defaultMessage="Back"
                id="wpzcyQG3XD"
                description="Button label to return from the mobile patient picker"
              />
            </TypographyPBold>
          </div>
          <PatientProfileList
            setOpen={setOpen}
            trackingSurface={trackingSurface}
            commandInputPlaceholder={commandInputPlaceholder}
            selectedSessionIds={selectedSessionIds}
            disabled={disabled}
            showSuggestedMatches
            onReviewSuggestedMatches={handleReviewSuggestedMatches}
            patientProfileAppointmentCandidates={
              patientProfileAppointmentCandidatesForSelection
            }
          />
        </DrawerContent>
      </Drawer>
      {reviewDialog}
    </>
  );
}
